import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, Share2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";

const SHARE_PROMPT_KEY = (id) => `rl_feedback_share_prompted_${id}`;

function alreadyPrompted(id) {
  try {
    return localStorage.getItem(SHARE_PROMPT_KEY(id)) === "1";
  } catch {
    return false;
  }
}

function markPrompted(id) {
  try {
    localStorage.setItem(SHARE_PROMPT_KEY(id), "1");
  } catch {
    /* ignore */
  }
}

/** Public trust badge — never shows raw points. */
export function TrustBadge({ badge, className = "" }) {
  if (!badge?.label) return null;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full bg-brand-green/10 px-2 py-0.5 text-xs font-semibold text-brand-green ${className}`}
      data-testid="trust-badge"
      title={badge.label}
    >
      <ShieldCheck className="h-3.5 w-3.5" />
      {badge.label}
    </span>
  );
}

export function FeedbackCard({ item, onAnswered }) {
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [sharePrompt, setSharePrompt] = useState(false);
  const [askCommunity, setAskCommunity] = useState(false);
  if (!item) return null;

  const finish = () => {
    onAnswered?.(item.id);
  };

  const skipShare = () => {
    markPrompted(item.id);
    setSharePrompt(false);
    finish();
  };

  const openShare = () => {
    markPrompted(item.id);
    setSharePrompt(false);
    if (item.branch_id) nav(`/app/refer/${item.branch_id}`);
    finish();
  };

  const skipAsk = () => {
    setAskCommunity(false);
    finish();
  };

  const openAsk = () => {
    const need = item.need_text || item.message || item.business_name || "";
    setAskCommunity(false);
    nav(`/app/requests/new?q=${encodeURIComponent(need ? `Looking for: ${need}` : "")}`);
    finish();
  };

  const answer = async (value) => {
    setBusy(true);
    try {
      await api.post(`/me/feedback/${item.id}/answer`, { answer: value });
      toast.success("Thanks — that helps the community.");
      if (value === 1 && item.branch_id && !alreadyPrompted(item.id)) {
        setSharePrompt(true);
      } else if (value === 3) {
        setAskCommunity(true);
      } else {
        finish();
      }
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  if (sharePrompt) {
    const name = item.business_name || "this business";
    return (
      <div className="rounded-[14px] border border-border bg-card p-4" data-testid={`feedback-share-prompt-${item.id}`}>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-green">Nice</p>
        <p className="mt-2 text-base font-semibold" data-testid="feedback-share-title">
          Refer {name} to a friend now
        </p>
        <p className="mt-1 text-sm text-muted-foreground">
          Share your link in one tap — skip anytime.
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          <Button className="bg-brand-gold text-brand-ink hover:bg-brand-gold-dark" onClick={openShare} data-testid="feedback-share-now-btn">
            <Share2 className="mr-2 h-4 w-4" /> Refer {name}
          </Button>
          <Button variant="ghost" onClick={skipShare} data-testid="feedback-share-skip-btn">
            Skip
          </Button>
        </div>
      </div>
    );
  }

  if (askCommunity) {
    return (
      <div className="rounded-[14px] border border-border bg-card p-4" data-testid={`feedback-ask-community-${item.id}`}>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-green">Got it</p>
        <p className="mt-2 text-base font-semibold">Also ask the community?</p>
        <p className="mt-1 text-sm text-muted-foreground">
          Post what you need — neighbors can refer a better fit.
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          <Button className="bg-brand-green hover:bg-brand-green-dark" onClick={openAsk} data-testid="feedback-ask-community-btn">
            Ask the community
          </Button>
          <Button variant="ghost" onClick={skipAsk} data-testid="feedback-ask-skip-btn">Skip</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-[14px] border border-border bg-card p-4" data-testid={`feedback-card-${item.id}`}>
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-green">Referral check-in</p>
      <p className="mt-2 text-base font-semibold" data-testid="feedback-question">{item.question}</p>
      {item.incentive?.text ? (
        <p className="mt-1 text-sm text-muted-foreground">{item.incentive.text}{item.incentive.value ? ` (${item.incentive.value})` : ""}</p>
      ) : null}
      <div className="mt-3 flex flex-col gap-2">
        {(item.answers || []).map((a) => (
          <Button
            key={a.value}
            variant="outline"
            disabled={busy}
            className="h-auto whitespace-normal justify-start py-2 text-left"
            onClick={() => answer(a.value)}
            data-testid={`feedback-answer-${a.value}`}
          >
            {a.label}
          </Button>
        ))}
      </div>
    </div>
  );
}

export function FeedbackLoginPrompt({ prompt, onDone }) {
  if (!prompt) return null;

  const dismissForever = async () => {
    try {
      await api.post(`/me/feedback/${prompt.id}/dismiss-prompt`);
      onDone?.();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-4 sm:items-center" data-testid="feedback-login-prompt">
      <div className="w-full max-w-md rounded-[16px] border border-border bg-background p-5 shadow-lg">
        <p className="text-sm font-semibold">Help the community</p>
        <p className="mt-1 text-sm text-muted-foreground">
          {prompt.incentive?.text || "A quick answer helps others know who to trust."}
          {prompt.incentive?.value ? ` ${prompt.incentive.value}` : ""}
        </p>
        <div className="mt-4">
          <FeedbackCard item={prompt} onAnswered={() => onDone?.()} />
        </div>
        <div className="mt-3 flex justify-between gap-2">
          <Button variant="ghost" size="sm" onClick={() => onDone?.()} data-testid="feedback-prompt-later">
            Later
          </Button>
          <Button variant="ghost" size="sm" onClick={dismissForever} data-testid="feedback-prompt-dismiss-forever">
            Don&apos;t ask about this again
          </Button>
        </div>
      </div>
    </div>
  );
}
